// src/lib/recognitions.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. Queries for marks (0046). The arithmetic lives in
//               marksMath.js and the catalogue in content/marks.js.
//
// Every read here returns `{ data, error }` and never folds an error into an
// empty list. A failed read of recognitions rendered as "nobody has been
// recognised this week" would tell a manager to go and recognise people who
// were recognised yesterday, which is the swallowed-error bug with a kinder
// face. See CLAUDE.md, the one line that has caused six silent bugs.

import { supabase } from './supabase'
import { markByKey } from '../content/marks'

export * from './marksMath'

const COLS = 'id, dealership_id, given_by, given_to, mark, note, step_number, created_at, seen_at'

// The store's marks since a date. RLS scopes this to the caller's store; the
// dealership filter is here so the index is used, not as the permission.
export async function listSince(dealershipId, sinceIso) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('recognitions')
    .select(COLS)
    .eq('dealership_id', dealershipId)
    .gte('created_at', sinceIso)
    .order('created_at', { ascending: false })
    .limit(200)
  return { data: error ? null : data || [], error }
}

// What has been given to one person, for their own Today and for the person
// page. Recent first.
export async function listFor(userId, limit = 20) {
  if (!userId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('recognitions')
    .select(COLS)
    .eq('given_to', userId)
    .order('created_at', { ascending: false })
    .limit(limit)
  return { data: error ? null : data || [], error }
}

// Give a mark. The step comes from the catalogue rather than the caller, so a
// "Trade done right" can never be filed against Step 3 by a stray argument.
export async function giveMark({ dealershipId, givenBy, givenTo, mark, note }) {
  const m = markByKey(mark)
  if (!m) return { data: null, error: 'That mark does not exist.' }
  if (!dealershipId) return { data: null, error: 'No dealership on your account.' }
  if (givenBy === givenTo) return { data: null, error: 'You cannot give yourself a mark.' }
  const text = String(note || '').trim()
  const { data, error } = await supabase
    .from('recognitions')
    .insert({
      dealership_id: dealershipId,
      given_by: givenBy,
      given_to: givenTo,
      mark: m.key,
      step_number: m.step,
      note: text ? text.slice(0, 160) : null,
    })
    .select(COLS)
    .single()
  return { data, error: error ? error.message || String(error) : null }
}

// Take one back. RLS allows the giver within a day and nobody else.
export async function undoMark(id) {
  const { error } = await supabase.from('recognitions').delete().eq('id', id)
  return { error: error ? error.message || String(error) : null }
}

// The recipient opened it. seen_at is the one column they are granted.
export async function markSeen(ids) {
  if (!ids || ids.length === 0) return { error: null }
  const { error } = await supabase
    .from('recognitions')
    .update({ seen_at: new Date().toISOString() })
    .in('id', ids)
    .is('seen_at', null)
  return { error: error ? error.message || String(error) : null }
}

// Names for the people in a list of marks. Givers are managers and owners, who
// are not in the floor list the screen already has. RLS on users scopes this
// to the store.
export async function listPeople(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))]
  if (unique.length === 0) return { data: [], error: null }
  const { data, error } = await supabase
    .from('users')
    .select('id, display_name, first_name, last_name, initials')
    .in('id', unique)
  return { data: error ? null : data || [], error }
}

// Today's morning routines across the floor. activity_manager_read lets a
// manager see these for their own people and nobody else.
export async function listMorningEvents(userIds, sinceIso) {
  if (!userIds || userIds.length === 0) return { data: [], error: null }
  const { data, error } = await supabase
    .from('activity_events')
    .select('user_id, kind, metadata, created_at')
    .eq('kind', 'morning_routine')
    .in('user_id', userIds)
    .gte('created_at', sinceIso)
  return { data: error ? null : data || [], error }
}

// Where the floor is in the thirteen steps.
export async function listFloorProgress(userIds) {
  if (!userIds || userIds.length === 0) return { data: [], error: null }
  const { data, error } = await supabase
    .from('step_progress')
    .select('user_id, status, updated_at, steps(number)')
    .in('user_id', userIds)
  return { data: error ? null : data || [], error }
}
